import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { Sheet } from 'react-modal-sheet';
import { CloseIcon } from '@/icons';
import { FormValue, Type } from './type';
import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { useTradeStore } from '@/store/trade-store';
import { usePrice } from '@/store/price';
import { useShallow } from 'zustand/shallow';
import { PriceState } from '../market/market';
import { usePairsByIndex } from './hooks/usePairsByIndex';
import { useGetLeverage } from './hooks/useGetLeverage';
import { formatDisplay } from '@/utils/format-display';
import { Detail } from './buy-sell/detail';

export function BuySell() {
    const { control, setValue } = useFormContext<FormValue>();
    const [isOpen, setOpen] = useState(false);
    const pairIndex = useWatch({ control, name: 'pairIndex' });
    const pair = usePairsByIndex(pairIndex);
    const { minLeverage, maxLeverage } = useGetLeverage(Number(pairIndex));
    const [searchParams, setSearchParams] = useSearchParams();

    const { isClose: disabled } = useCheckClosePair(Number(pairIndex));
    const prices = usePrice(useShallow((state: PriceState) => state.data));
    const spreads = useTradeStore((state) => state.booking);
    const spread = useMemo(() => {
        return spreads.find((item) => String(item.pair_index) === pairIndex)
            ?.spread;
    }, [pairIndex, spreads]);
    const price = useMemo(() => {
        return Number(prices[pairIndex]?.price || 0);
    }, [pairIndex, prices]);

    const handleGoDetail = useCallback(
        (type: Type) => {
            setValue('type', type);
            setOpen(true);
        },
        [setValue]
    );

    useEffect(() => {
        const amountParam = searchParams.get('amount');
        const leverageParam = searchParams.get('leverage');
        const typeParam = searchParams.get('type');
        const tpParam = searchParams.get('tp');

        if (!amountParam || !leverageParam || !typeParam) return;
        // These are plain URL query params, not signed/opaque — validate before
        // trusting them (a crafted deep link could otherwise inject an invalid
        // side or an out-of-range leverage).
        if (typeParam !== Type.Long && typeParam !== Type.Short) return;
        const leverageNum = Number(leverageParam);
        if (!Number.isFinite(leverageNum)) return;
        const clampedLeverage = Math.min(
            maxLeverage,
            Math.max(minLeverage, leverageNum)
        );

        setValue('amount', amountParam);
        setValue('leverage', clampedLeverage);
        setValue('type', typeParam);
        if (tpParam) {
            setValue('tp', tpParam);
        }
        setOpen(true);

        const next = new URLSearchParams(searchParams);
        next.delete('amount');
        next.delete('leverage');
        next.delete('type');
        next.delete('tp');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, setValue, minLeverage, maxLeverage]);

    const data = useMemo(() => {
        const base = Number(price) || 0;
        const s = Number(spread) / 100;
        const sellPrice = base * (1 - s);
        const buyPrice = base * (1 + s);
        return { sellPrice, buyPrice, spreadPrice: buyPrice - price };
    }, [price, spread]);

    const handleClose = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('suggest');
        setSearchParams(next, { replace: true });
        setOpen(false);
    }, [searchParams, setSearchParams]);

    return (
        <div className="fixed bottom-[72px] left-0 right-0 bg-white border-t border-slate-100 px-4 py-3 z-40">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => handleGoDetail(Type.Long)}
                    disabled={disabled}
                    className="disabled:opacity-50 flex-1 bg-green-500 text-white rounded-xl py-3 px-4 flex flex-col items-center justify-center shadow-glow-green active:scale-[0.98] transition-all"
                >
                    <span className="text-[11px] font-bold opacity-90 uppercase tracking-wider mb-0.5">
                        Long
                    </span>
                    <span className="text-[17px] font-bold">
                        {formatDisplay(data.buyPrice, {
                            decimalToShow: pair?.decimalsToShow,
                        })}
                    </span>
                </button>
                <div className="flex flex-col items-center justify-center shrink-0 px-1">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                        Spread
                    </p>
                    <p className='class="text-base font-bold text-slate-800 tabular-nums"'>
                        {formatDisplay(data.spreadPrice, {
                            decimalToShow: pair?.decimalsToShow,
                        })}
                    </p>
                </div>
                <button
                    onClick={() => handleGoDetail(Type.Short)}
                    disabled={disabled}
                    className="disabled:opacity-50 flex-1 bg-red-500 text-white rounded-xl py-3 px-4 flex flex-col items-center justify-center shadow-glow-red active:scale-[0.98] transition-all"
                >
                    <span className="text-[11px] font-bold opacity-90 uppercase tracking-wider mb-0.5">
                        Short
                    </span>
                    <span className="text-[17px] font-bold">
                        {formatDisplay(data.sellPrice, {
                            decimalToShow: pair?.decimalsToShow,
                        })}
                    </span>
                </button>
            </div>
            <Sheet
                isOpen={isOpen}
                onClose={() => {
                    handleClose();
                }}
                detent="content"
            >
                <Sheet.Container>
                    <Sheet.Header>
                        <div className="flex items-center justify-center p-4 relative">
                            <Sheet.DragIndicator />
                            <button
                                className="absolute right-4 top-3 cursor-pointer"
                                onClick={() => {
                                    handleClose();
                                }}
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </Sheet.Header>
                    <Sheet.Content>
                        <Detail
                            onClose={() => {
                                handleClose();
                            }}
                        />
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop
                    onTap={() => {
                        handleClose();
                    }}
                />
            </Sheet>
        </div>
    );
}
